import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  useTheme,
  alpha,
  Divider
} from '@mui/material';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import { PieChart, Pie, Cell, Tooltip as ChartTooltip, ResponsiveContainer } from 'recharts';

// --- Mock Data Embedded ---
interface ZoneEntry {
  zone: string;
  percentage: number;
  kWh: number;
}

const mockSubmeteringData: ZoneEntry[] = [
  { zone: 'Kitchen', percentage: 45, kWh: 1800 },
  { zone: 'Dining Area', percentage: 30, kWh: 1200 },
  { zone: 'Storage', percentage: 10, kWh: 400 },
  { zone: 'Office', percentage: 15, kWh: 600 }
];
// --- End Embedded Mock Data ---

const EnergyDistribution = () => {
  const theme = useTheme();

  // Use embedded mock data
  const submeteringData = mockSubmeteringData;

  const COLORS = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.info.main,
    theme.palette.warning.main,
    alpha(theme.palette.primary.main, 0.7),
    alpha(theme.palette.secondary.main, 0.7),
    alpha(theme.palette.success.main, 0.7),
  ];

  const totalEnergy = submeteringData.reduce((acc, zone) => acc + zone.kWh, 0);

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
        Energy Distribution by Zone
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Box sx={{ height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={submeteringData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={150}
                  fill="#8884d8"
                  dataKey="kWh"
                  nameKey="zone"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {submeteringData.map((_entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <ChartTooltip formatter={(value: number, name: string) => [`${value.toLocaleString()} kWh`, name]} />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
              height: '100%',
            }}
          >
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Energy Distribution Breakdown
              </Typography>

              <Box sx={{ maxHeight: 300, overflowY: 'auto', pr: 1 }}>
                {submeteringData.map((zone, index) => (
                  <Box key={index} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            bgcolor: COLORS[index % COLORS.length],
                            borderRadius: '50%',
                            mr: 1
                          }}
                        />
                        {zone.zone}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {zone.kWh.toLocaleString()} kWh
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        height: 6,
                        bgcolor: alpha(COLORS[index % COLORS.length], 0.2),
                        borderRadius: 3,
                        position: 'relative',
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          height: '100%',
                          width: `${zone.percentage}%`,
                          bgcolor: COLORS[index % COLORS.length],
                          borderRadius: 3
                        }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Total Energy:
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {totalEnergy.toLocaleString()} kWh
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ p: 3, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              <TipsAndUpdatesIcon sx={{ mr: 1, verticalAlign: 'bottom', color: theme.palette.primary.main }} />
              Optimization Insights
            </Typography>

            <Typography variant="body2">
              {submeteringData.length > 0 ? (
                `Your highest energy consumption is in the ${submeteringData[0].zone} area (${submeteringData[0].percentage}% of total). Consider prioritizing energy efficiency upgrades in this area for maximum impact. For example, upgrading to LED lighting or optimizing HVAC systems could significantly reduce your overall consumption.`
              ) : (
                'Submetering data is not available.'
              )}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EnergyDistribution; 